import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  formLabelClass,
  formControlClass,
  formGridClass,
  requiredMarkClass,
  btnPrimaryClass,
  alertErrorClass,
  spinnerClass,
} from "./dashboardStyles";
import { UpdateMyProfileAction } from "../../store/action";
import { useMyProfile } from "../../hooks/useMyProfile";
import { notify, notifySaveError } from "../../utils/notify";

/**
 * "My details" on the Settings page.
 *
 * These four fields exist to be *used*, not filed: the name and phone pre-fill
 * the booking form so a signed-in customer is never asked for what we already
 * hold, and the city of residence opens the business search on where they
 * actually are. The help text under each says so — a profile field with no
 * visible effect is one nobody fills in.
 *
 * Email is shown but not editable. It is the login identity, and moving it needs
 * proof of the new mailbox — a separate job, and deliberately not a text box
 * next to the phone number.
 */
const ProfilePanel = () => {
  const { t } = useTranslation();
  const { profile, loading, error: loadError } = useMyProfile();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Seed the form once the profile lands. Never afterwards: re-seeding would
  // fight the person typing into the very fields being edited.
  useEffect(() => {
    if (!profile) return;
    setForm({
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      phone: profile.phone || "",
      city: profile.city || "",
    });
  }, [profile]);

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const data = await UpdateMyProfileAction({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        // Blank means "nothing on file"; the API stores that as null.
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
      });
      // Re-seed from the response rather than from what was submitted: the API
      // trims, so the form must show what was actually stored.
      setForm({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phone: data.phone || "",
        city: data.city || "",
      });
      notify.success(t("Dashboard.Profile.saved"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.Profile.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cardClass}>
      <div className={cardHeaderClass}>
        <h3 className={cardTitleClass}>{t("Dashboard.Profile.title")}</h3>
      </div>

      <div className={cardBodyClass}>
        {loading ? (
          <div className="flex justify-center py-8">
            <div
              role="status"
              aria-label={t("Dashboard.Profile.loading")}
              className={spinnerClass}
            />
          </div>
        ) : loadError || !form ? (
          <div className={alertErrorClass} role="alert">
            <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
            {t("Dashboard.Profile.loadError")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className={formGridClass}>
              <div>
                <label className={formLabelClass} htmlFor="profile-firstName">
                  {t("Dashboard.Profile.firstName")}
                  <span className={requiredMarkClass}> *</span>
                </label>
                <input
                  id="profile-firstName"
                  name="firstName"
                  type="text"
                  className={formControlClass}
                  value={form.firstName}
                  required
                  maxLength={100}
                  onChange={(event) => setField("firstName", event.target.value)}
                />
              </div>

              <div>
                <label className={formLabelClass} htmlFor="profile-lastName">
                  {t("Dashboard.Profile.lastName")}
                  <span className={requiredMarkClass}> *</span>
                </label>
                <input
                  id="profile-lastName"
                  name="lastName"
                  type="text"
                  className={formControlClass}
                  value={form.lastName}
                  required
                  maxLength={100}
                  onChange={(event) => setField("lastName", event.target.value)}
                />
              </div>

              <div>
                <label className={formLabelClass} htmlFor="profile-phone">
                  {t("Dashboard.Profile.phone")}
                </label>
                <input
                  id="profile-phone"
                  name="phone"
                  type="tel"
                  className={formControlClass}
                  value={form.phone}
                  maxLength={20}
                  placeholder={t("Dashboard.Profile.phonePlaceholder")}
                  onChange={(event) => setField("phone", event.target.value)}
                />
                <p className="mt-1 text-xs text-ink-400">
                  {t("Dashboard.Profile.phoneHelp")}
                </p>
              </div>

              <div>
                <label className={formLabelClass} htmlFor="profile-city">
                  {t("Dashboard.Profile.city")}
                </label>
                <input
                  id="profile-city"
                  name="city"
                  type="text"
                  className={formControlClass}
                  value={form.city}
                  maxLength={100}
                  placeholder={t("Dashboard.Profile.cityPlaceholder")}
                  onChange={(event) => setField("city", event.target.value)}
                />
                <p className="mt-1 text-xs text-ink-400">
                  {t("Dashboard.Profile.cityHelp")}
                </p>
              </div>
            </div>

            <div className="flex justify-end border-t border-ink-100 pt-5">
              <button
                type="submit"
                className={`${btnPrimaryClass} min-h-11 max-sm:w-full`}
                disabled={saving}
              >
                {saving
                  ? t("Dashboard.Profile.saving")
                  : t("Dashboard.Profile.save")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePanel;
